import { captureException } from '@/lib/sentry/ui-error-reporter'

/**
 * Teardown callbacks that run when the session ends.
 *
 * `signOut` clears the Clerk session, the query cache, `@bitlyft/auth-tenant` and
 * the legacy `@bitlyft/user` key — but a feature that keeps its OWN localStorage
 * key survives it, which on a shared analyst workstation is the next user's
 * problem. This is the seam for those keys: a feature registers its teardown at
 * module scope, and `shared/` never has to import a feature to reach it (A-02 —
 * `shared` is a leaf).
 *
 * **A feature whose module was never loaded this session registers nothing**, so
 * its key survives the sign-out. That is only safe for keys namespaced by USER,
 * which is why dashboard drafts key on `${tenant}:${userId}:${dashboardId}` —
 * another user on the same machine cannot read them either way. Everything else
 * still leaking here (every tenant-namespaced feature key) is the general gap
 * ADR 0024's ticket noted for follow-up; this file is where that fix lands, one
 * `registerSignOutTeardown` call at a time.
 */
const teardowns = new Set<() => void>()

/**
 * Register a callback to run on sign-out. Returns an unregister function — call
 * sites that register at module scope never need it; tests do.
 */
export const registerSignOutTeardown = (teardown: () => void): (() => void) => {
	teardowns.add(teardown)
	return () => {
		teardowns.delete(teardown)
	}
}

/**
 * Run every registered teardown. One that throws is reported and skipped rather
 * than allowed to strand the rest — a half-finished sign-out is the failure this
 * whole seam exists to prevent.
 */
export const runSignOutTeardowns = (): void => {
	for (const teardown of teardowns) {
		try {
			teardown()
		} catch (error) {
			captureException(error, {
				source: 'state',
				action: 'sign_out_teardown',
				component: 'sign-out-teardown.ts',
			})
		}
	}
}
